import { Eye } from "lucide-react";
import { memo, useMemo } from "react";
import { useLocation } from "wouter";

import type { AdminOrderPageResponseDTO } from "@/common/api";
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TruncateTooltip,
  Card,
  CardContent,
} from "@/common/components";
import { ROUTES } from "@/common/config";
import { formatDatetime, getStatusData } from "@/common/lib";

interface ModerationOrdersTableProps {
  items: AdminOrderPageResponseDTO[];
  isLoading: boolean;
  onRowClick: (itemId: string | undefined) => void;
}

export const ModerationOrdersTable = memo(function ModerationOrdersTable({
  items,
  isLoading,
  onRowClick,
}: ModerationOrdersTableProps) {
  const [, setLocation] = useLocation();

  const rows = useMemo(
    () => (
      <>
        {items.map((item) => {
          return (
            <TableRow
              key={item.id}
              className="cursor-pointer hover:bg-gray-50"
              onClick={() => onRowClick(item.id)}
            >
              <TableCell className="text-center font-medium">{item.orderNumber}</TableCell>
              <TableCell className="text-center ">
                {item.tradeBegin && item.tradeEnd ? (
                  <div className="flex flex-col">
                    <span className="text-sm text-nowrap">c {formatDatetime(item.tradeBegin)}</span>
                    <span className="text-sm text-nowrap">по {formatDatetime(item.tradeEnd)}</span>
                  </div>
                ) : (
                  "-"
                )}
              </TableCell>
              <TableCell className="text-center">
                <div className="flex flex-col items-center">
                  <TruncateTooltip
                    text={item.loadingStation ?? "-"}
                    className="text-sm font-medium text-center"
                  />
                  {item.loadingRoad && <p className="text-sm">({item.loadingRoad})</p>}
                </div>
              </TableCell>
              <TableCell className="text-center">
                <Badge className={getStatusData(item.status).color}>
                  {getStatusData(item.status).label}
                </Badge>
              </TableCell>
              <TableCell className="text-center">
                {item.stageBegin && item.stageEnd ? (
                  <div className="flex flex-col">
                    <span className="text-sm text-nowrap">c {formatDatetime(item.stageBegin)}</span>
                    <span className="text-sm text-nowrap">по {formatDatetime(item.stageEnd)}</span>
                  </div>
                ) : (
                  "-"
                )}
              </TableCell>
              <TableCell className="text-center">{item.participantsCount ?? "-"}</TableCell>
              <TableCell className="text-center flex flex-col items-center">
                <TruncateTooltip
                  text={item.cargoName ?? "-"}
                  className="text-sm font-medium text-center"
                />
                <p className="text-sm">({item.cargoWeight} т.)</p>
              </TableCell>
              <TableCell className="text-center">
                <TruncateTooltip
                  text={item.wagonType ?? "-"}
                  className="text-sm font-medium text-center"
                />
                <p className="text-sm">{item.totalWagons && `(${item.totalWagons} в.)`}</p>
              </TableCell>
              <TableCell className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    const basePath = ROUTES.ORDER_ID.replace(":id", String(item.id));
                    setLocation(`${basePath}?participant=1`);
                  }}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </>
    ),
    [items, onRowClick, setLocation],
  );

  return (
    <Card>
      <CardContent className="p-0 w-[calc(100vw-72px)] md:w-[calc(100vw-336px)]">
        {isLoading ? (
          <div className="text-center py-8">
            <p className="caption">Загрузка заявок...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8">
            <p className="caption">Нет инициированных заявок</p>
          </div>
        ) : (
          <Table className="">
            <TableHeader>
              <TableRow>
                <TableHead className="text-start text-xs font-semibold leading-4 text-Text-secondary">
                  № заявки
                </TableHead>
                <TableHead className="text-center">Период проведения</TableHead>
                <TableHead className="text-center w-44">Станция отправления (дорога)</TableHead>
                <TableHead className="text-center">Этап</TableHead>
                <TableHead className="text-center">Период этапа</TableHead>
                <TableHead className="text-center">Количество участников</TableHead>
                <TableHead className="text-center">Груз</TableHead>
                <TableHead className="text-center">Вагоны</TableHead>
                <TableHead className="text-center">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>{rows}</TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
});
